/**
 * Visit status machine (spec §5.2). Pure functions used by the API (enforcer)
 * and the UI (button visibility). Timestamps are ISO strings or null.
 */
export const VISIT_STATUSES = [
    'requested',
    'scheduled',
    'completed',
    'validated',
    'cancelled',
];
export const VISIT_ACTIONS = [
    'approve',
    'check_in',
    'check_out',
    'admin_sign',
    'cancel',
    'reschedule',
    'close',
];
export const OVERDUE_AFTER_MS = 4 * 60 * 60 * 1000;
const NEXT_STATUS = {
    approve: 'scheduled',
    check_in: 'scheduled',
    check_out: 'completed',
    admin_sign: 'validated',
    cancel: 'cancelled',
    reschedule: null,
    close: null,
};
function fail(v, reason) {
    return { ok: false, next: v.status, reason };
}
function ok(v, action) {
    return { ok: true, next: NEXT_STATUS[action] ?? v.status };
}
function cancelledOutcomeGuard(v) {
    if (v.status !== 'cancelled')
        return 'only cancelled visits can be rescheduled or closed';
    if (v.rescheduledToId !== null)
        return 'already rescheduled';
    return v.closedAt ? 'already closed' : null;
}
const GUARDS = {
    approve: (v) => (v.status === 'requested' ? null : 'only requested visits can be approved'),
    check_in: (v) => {
        if (v.status !== 'scheduled')
            return 'only scheduled visits can be checked in';
        return v.checkInAt ? 'already checked in' : null;
    },
    check_out: (v) => {
        if (v.status !== 'scheduled' || !v.checkInAt)
            return 'check in first';
        return v.checkOutAt ? 'already checked out' : null;
    },
    admin_sign: (v) => (v.status === 'completed' ? null : 'only completed visits can be signed'),
    cancel: (v) => {
        const notStarted = v.status === 'scheduled' && !v.checkInAt;
        if (v.status === 'requested' || notStarted)
            return null;
        return 'only requested or not-yet-started scheduled visits can be cancelled';
    },
    reschedule: cancelledOutcomeGuard,
    close: cancelledOutcomeGuard,
};
/** Decides whether `action` is legal for the visit in its current state. */
export function canTransition(v, action) {
    const reason = GUARDS[action](v);
    return reason === null ? ok(v, action) : fail(v, reason);
}
/** Display state that folds check-in progress and cancellation outcome into the status. */
export function derivedState(v, now = new Date()) {
    if (v.status === 'scheduled') {
        if (v.checkInAt && !v.checkOutAt)
            return 'in_progress';
        if (!v.checkInAt && now.getTime() - Date.parse(v.scheduledAt) > OVERDUE_AFTER_MS) {
            return 'overdue';
        }
    }
    if (v.status === 'cancelled') {
        if (v.rescheduledToId !== null)
            return 'rescheduled';
        if (v.closedAt)
            return 'closed';
    }
    return v.status;
}
/** Statuses considered work in progress for the WIP filter. */
export const WIP_STATUSES = ['requested', 'scheduled', 'completed'];
//# sourceMappingURL=visit-state.js.map